import isEmpty from 'lodash/isEmpty';
import compact from 'lodash/compact';
import { iterate, mergeObjects, unpackFn } from './util';
import { length } from './validators/length';
import { listSize } from './validators/listSize';
import { noDuplicates } from './validators/noDuplicates';
import { noneOf } from './validators/noneOf';
import { numeric } from './validators/numeric';
import { presence } from './validators/presence';
import { boundaries } from './validators/boundaries';
import { fileAccept } from './validators/fileAccept';
import { scientificNotation, noScientificNotation } from './validators/scientificNotation';
export { decimals, significantDigits } from './validators/numeric';

// Declares a simple validation rule for a field, e.g.
//
// const obj = {name: ''}
// const rules = {name: [rule('presence')]}
// --> validate(obj, rules) = {name: ['cannot be blank]}
export var rule = function rule(ruleType) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return Object.assign({}, options, {
    $ruleType: ruleType
  });
};

// Declares a rule that is applied to every element of
// a collection, e.g.
//
// const obj = {names: ['bob', 'joe', '']}
// const rules = {names: [each(rule('presence'))]}
// --> validate(obj, rules) = {names: {2: ['cannot be blank]}}
export var each = function each(rule) {
  return {
    $each: rule
  };
};

// Declares a rule that is applied to the collection and
// keyed on $errors
//
// const obj = {names: ['bob', 'joe', 'sue']}
// const rules = {names: [onSelf(rule('listSize', {min: 4}))]}
// --> validate(obj, rules) = {names: {$errors: ['must have four]}}
export var onSelf = function onSelf(rule) {
  return {
    $onSelf: rule
  };
};
var validators = {};
var mergeErrors = function mergeErrors(errors) {
  var compactedErrors = compact(errors);
  if (isEmpty(compactedErrors)) {
    return;
  }
  if (typeof compactedErrors[0] === 'string') {
    return compactedErrors;
  }
  return mergeObjects(compactedErrors);
};
var _errorsForRule = function errorsForRule(value, rule) {
  if (rule.hasOwnProperty('$onSelf')) {
    var result = _errorsForRule(value, rule.$onSelf);
    return result ? {
      $errors: [result]
    } : null;
  }
  if (rule.hasOwnProperty('$ruleType')) {
    var validator = validators[rule.$ruleType];
    return validator(value, rule);
  } else if (rule.hasOwnProperty('$each')) {
    if (!value) {
      return;
    }
    var errors = {};
    iterate(value, function (item, key) {
      var result = _errorsForRule(item, rule.$each);
      if (result) {
        errors[key] = result;
      }
    });
    return errors;
  } else {
    return _validate(value, rule);
  }
};

// the main validation function exported by the library
var _validate = function validate(objToValidate, rules) {
  var errors = {};
  var unpackedRules = unpackFn(objToValidate, rules);
  Object.keys(unpackedRules).forEach(function (key) {
    var valForKey = objToValidate[key];
    var rulesForKey = unpackedRules[key];
    var result;
    if (Array.isArray(rulesForKey)) {
      result = mergeErrors(rulesForKey.map(function (rule) {
        return _errorsForRule(valForKey, rule);
      }));
    } else {
      result = _validate(valForKey, rulesForKey);
    }
    if (result && !isEmpty(result)) {
      errors[key] = result;
    }
  });
  return errors;
};
export { _validate as validate };
export var addValidator = function addValidator(key, validator) {
  validators[key] = validator;
};
addValidator('length', length);
addValidator('listSize', listSize);
addValidator('noDuplicates', noDuplicates);
addValidator('noneOf', noneOf);
addValidator('numeric', numeric);
addValidator('presence', presence);
addValidator('boundaries', boundaries);
addValidator('fileAccept', fileAccept);
addValidator('scientificNotation', scientificNotation);
addValidator('noScientificNotation', noScientificNotation);